import React, { useState, useEffect, useRef } from "react";
import { Button, message, Spin, notification } from "antd";
import { EditOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { IAddOrEditSpaceProps } from "./types";
import DrawerComponent from "components/Drawer";
import "./style.scss";
import { fetchEditMetadata } from "./AddOrEditSpaceApi";
import { useMsal } from "@azure/msal-react";
import { getSagemakerDomainByDisplayName } from "utils/utils";

const contentStyle: React.CSSProperties = {
  padding: 50,
  borderRadius: 4,
};

const AddOrEditSpace: React.FC<IAddOrEditSpaceProps> = ({
  mode,
  title,
  buttonText,
  children,
  buttonProps,
  onSuccess,
  onDrawerOpen,
  onDrawerClose,
  autoTrigger = false,
  editPayload = {
    bucketName: "",
    spaceName: "",
    region: "",
    domainName: "",
    actualBucketName: "",
  },

  domainName = "",
  parentTemplateRepo,
  galleryProfileType,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState<any>(null); // TODO:

  const msalInstance = useMsal();
  const label = buttonText || (mode === "create" ? "Create" : "Edit");

  interface DomainConfig {
    display_name: string;
    sagemaker_domain?: string;
  }

  const sagemakerDomain = getSagemakerDomainByDisplayName(domainName);
  const hasAutoTriggered = useRef(false);

  const handleClick = async () => {
    if (mode === "create") {
      setEditData(null); // Clear any previous data
      setDrawerOpen(true);
    } else { // for edit flow
      setLoading(true);
      notification.open({
        message: "Important!",
        description: "Team members must be manually added or removed from the respective GitHub project while updating the project owners.",
        duration: 10, // Keeps the notification open until manually closed
        placement: "top",
        btn: null,
        closeIcon: <button style={{ cursor: "pointer", marginRight: "20px", background: "#1890ff", border: "none", borderRadius: "2px", color: "white" }} onClick={() => notification.destroy()}>Close</button>,
      });
      setDrawerOpen(true);
      onDrawerOpen?.();
      try {
        const { bucketName, spaceName, region, domainName } = editPayload;
        const data = await fetchEditMetadata(msalInstance, bucketName, spaceName, region, domainName);
        setEditData(data);
      } catch (error) {
        setDrawerOpen(false);
        message.error("Failed to fetch edit data, Try again later");
        console.error("Failed to fetch edit data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  // When used as a programmatic trigger (no button), auto-open on mount
  useEffect(() => {
    if (autoTrigger && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handleClick();
    }
  }); // runs after every render but ref guards it to fire exactly once

  const renderTrigger = () => (
    autoTrigger ? null : (
      <Button
        type={mode === "create" ? "primary" : "link"}
        icon={mode === "create" ? <PlusOutlined /> : <EditOutlined />}
        onClick={handleClick}
        {...buttonProps}
        className="add-or-edit-space-btn"
      >
        <span className="add-or-edit-space-label">{label}</span>
      </Button>
    )
  );

  return (
    <>
      {renderTrigger()}

      {drawerOpen && (
        <DrawerComponent
          openMetadataForm={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            notification.destroy();
            onDrawerClose?.();
          }}
          editData={editData} // Pass edit data if available
          mode={mode}
          loading={loading}
          domainName={domainName}
          sagemakerDomain={sagemakerDomain}
          onSuccess={onSuccess}
          bucketTechName={editPayload?.actualBucketName}
          parentTemplateRepo={parentTemplateRepo}
          galleryProfileType={galleryProfileType}
        />
      )}
    </>
  );
};

export default AddOrEditSpace;
